/**
 * Automations (TRUS-686): org-level rules — "When <event> [and conditions]
 * then <actions>" — on the system-event catalog (contract section E).
 *
 * Mounted as the "Automations" tab of the Workflows dashboard and on the
 * existing `automation-rules` page. List, create / edit, enable / disable,
 * Test (a dry run against a synthetic event: shows what it would do) and Run
 * history. Org admins only: a 403 `admin_only` shows a plain explanation
 * instead of the list. Platform-event triggers are only offered to platform
 * admins the server allows.
 *
 * This replaces the pre-TRUS-686 editor (free-form trigger_type + JSON
 * actions); old rules still read through the defensive parser.
 */

import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { FlaskConical, History, Loader2, Pencil, Plus, ShieldAlert, Trash2, Wand2 } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { systemEventsApi, type SystemEventsApiError } from '@/services/systemEventsApi';
import {
  automationRulesApi,
  type AutomationRule,
  type AutomationRuleInput,
  type AutomationRun,
  type AutomationTestResult,
} from '@/services/automationRulesApi';
import { useIsPlatformAdmin } from '@/components/messaging/systemEvents/ChannelEventsDialog';
import { systemEventTypesKey, typeLabel, type TFn } from '@/components/messaging/systemEvents/eventCatalog';
import { phiRequiresPrivateMessage } from './notify/notifySteps';
import {
  AutomationRuleEditor,
  actionTypeLabel,
  emptyRuleInput,
  ruleToInput,
} from './automations/AutomationRuleEditor';

export const automationRulesKey = ['automation-rules'] as const;
export const automationRunsKey = (id: number) => ['automation-rule-runs', id] as const;

export function isAdminOnlyError(err: unknown): boolean {
  const e = err as SystemEventsApiError | undefined;
  return e?.code === 'admin_only' || e?.status === 403;
}

export function automationErrorMessage(err: unknown, t: TFn): string {
  const e = err as SystemEventsApiError | undefined;
  switch (e?.code) {
    case 'admin_only':
      return t('workflows.automations.errors.adminOnly', 'Only org admins can manage automations.');
    case 'phi_requires_private_channel':
      return phiRequiresPrivateMessage(t);
    case 'invalid_filter':
      return t(
        'workflows.automations.errors.invalidFilter',
        'One of the programs or people in the conditions is not in this organization any more. Check them and try again.',
      );
    case 'unknown_event_type':
      return t('workflows.automations.errors.unknownEvent', 'That event is no longer available. Pick another one.');
    case 'unknown_template_variable':
      return t(
        'workflows.automations.errors.unknownVariable',
        'A message uses a variable that is not allowed. Use only the variables offered under the message box.',
      );
    case 'invalid_channels':
      return t('workflows.automations.errors.invalidChannels', 'A "Notify staff" action needs the bell, push, or both.');
    case 'no_recipients':
      return t('workflows.automations.errors.noRecipients', 'An action has nobody to send to. Pick at least one recipient.');
    case 'invalid_action':
      return t('workflows.automations.errors.invalidAction', 'One of the actions is not set up completely. Check each action and try again.');
    case 'invalid_name':
      return t('workflows.automations.errors.invalidName', 'Give the automation a name (up to 200 characters).');
    case 'rule_not_found':
      return t('workflows.automations.errors.notFound', 'This automation no longer exists.');
    case 'not_a_system_event_rule':
      return t(
        'workflows.automations.errors.legacyRule',
        'This is an older automation that cannot be edited here. Delete it and create a new one.',
      );
    case 'cannot_subscribe':
    case 'platform_admin_only':
      return t('workflows.automations.errors.platformOnly', 'Only platform admins can use platform events.');
    default:
      break;
  }
  if (e?.status === 403) return t('workflows.automations.errors.adminOnly', 'Only org admins can manage automations.');
  if (e?.status === 404) return t('workflows.automations.errors.notFound', 'This automation no longer exists.');
  return t('workflows.automations.errors.generic', 'Something went wrong. Please try again.');
}

function runStatusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (status === 'failed') return 'destructive';
  if (status === 'skipped') return 'outline';
  return 'secondary';
}

function formatWhen(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

// ---------------------------------------------------------------------------

function TestDialog({ rule, onClose }: { rule: AutomationRule; onClose: () => void }) {
  const { t } = useAppTranslation();
  const query = useQuery<AutomationTestResult>({
    queryKey: ['automation-rule-test', rule.id],
    queryFn: () => automationRulesApi.test(rule.id),
    retry: false,
    gcTime: 0,
  });
  const result = query.data;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="automation-test-dialog">
        <DialogHeader>
          <DialogTitle>{t('workflows.automations.testTitle', 'Test “{{name}}”', { name: rule.name })}</DialogTitle>
          <DialogDescription>
            {t('workflows.automations.testDescription', 'A dry run with a made-up event. Nothing is posted, sent or created.')}
          </DialogDescription>
        </DialogHeader>
        {query.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            {t('workflows.automations.testing', 'Running the test…')}
          </div>
        ) : query.isError ? (
          <p className="text-sm text-destructive" role="alert">
            {automationErrorMessage(query.error, t)}
          </p>
        ) : result ? (
          <div className="space-y-3" data-testid="automation-test-result">
            {(result.matched === false || result.conditionsMatched === false) && (
              <p className="text-sm">{t('workflows.automations.testNoMatch', 'This event would not match the conditions, so nothing would happen.')}</p>
            )}
            {result.summary && <p className="text-sm">{result.summary}</p>}
            {result.actions.length > 0 ? (
              <>
                <p className="text-sm font-medium">{t('workflows.automations.testWouldDo', 'It would:')}</p>
                <ol className="list-decimal space-y-1 pl-5 text-sm">
                  {result.actions.map((a, i) => (
                    <li key={i}>
                      <span className="font-medium">
                        {(['post_to_channel', 'notify_staff', 'create_task', 'send_email'] as const).includes(a.type as any)
                          ? actionTypeLabel(a.type as any, t)
                          : a.type}
                      </span>
                      {a.text && <span className="text-muted-foreground"> — {a.text}</span>}
                      {a.status && (
                        <Badge variant={runStatusVariant(a.status)} className="ml-2 text-[10px]">
                          {a.status === 'would_run'
                            ? t('workflows.automations.wouldRun', 'Would run')
                            : a.status === 'skipped'
                              ? t('workflows.automations.runSkipped', 'Skipped')
                              : a.status}
                        </Badge>
                      )}
                      {a.skippedReason && (
                        <div className="text-xs text-muted-foreground" data-testid="automation-test-skipped-reason">
                          {t('workflows.automations.skippedBecause', 'Skipped: {{reason}}', { reason: a.skippedReason })}
                        </div>
                      )}
                    </li>
                  ))}
                </ol>
              </>
            ) : (
              result.matched !== false &&
              result.conditionsMatched !== false &&
              !result.summary && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground">{t('workflows.automations.testRaw', 'Details')}</summary>
                  <pre className="mt-2 max-h-64 overflow-auto rounded bg-muted p-2">{JSON.stringify(result.raw, null, 2)}</pre>
                </details>
              )
            )}
          </div>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('workflows.automations.close', 'Close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RunsDialog({ rule, types, onClose }: { rule: AutomationRule; types: any[]; onClose: () => void }) {
  const { t } = useAppTranslation();
  const query = useQuery<AutomationRun[]>({
    queryKey: automationRunsKey(rule.id),
    queryFn: () => automationRulesApi.runs(rule.id, 50),
    retry: false,
  });
  const runs = query.data ?? [];
  const statusLabel = (s: string) =>
    s === 'ok'
      ? t('workflows.automations.runOk', 'Ran')
      : s === 'failed'
        ? t('workflows.automations.runFailed', 'Failed')
        : s === 'skipped'
          ? t('workflows.automations.runSkipped', 'Skipped')
          : s;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl" data-testid="automation-runs-dialog">
        <DialogHeader>
          <DialogTitle>{t('workflows.automations.historyTitle', 'Run history: {{name}}', { name: rule.name })}</DialogTitle>
          <DialogDescription>{t('workflows.automations.historyDescription', 'The last 50 times this automation ran.')}</DialogDescription>
        </DialogHeader>
        {query.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            {t('workflows.automations.loading', 'Loading…')}
          </div>
        ) : query.isError ? (
          <p className="text-sm text-destructive" role="alert">
            {automationErrorMessage(query.error, t)}
          </p>
        ) : runs.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="automation-runs-empty">
            {t('workflows.automations.noRuns', 'It has not run yet.')}
          </p>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full text-sm" data-testid="automation-runs-table">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-1 pr-3 font-medium">{t('workflows.automations.colWhen', 'When')}</th>
                  <th className="py-1 pr-3 font-medium">{t('workflows.automations.colEvent', 'Event')}</th>
                  <th className="py-1 pr-3 font-medium">{t('workflows.automations.colStatus', 'Result')}</th>
                  <th className="py-1 font-medium">{t('workflows.automations.colError', 'Error')}</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id} className="border-b align-top last:border-0">
                    <td className="py-1.5 pr-3 whitespace-nowrap">{formatWhen(r.created_at)}</td>
                    <td className="py-1.5 pr-3">{r.event_type ? typeLabel(r.event_type, types, t) : ''}</td>
                    <td className="py-1.5 pr-3">
                      <Badge variant={runStatusVariant(r.status)}>{statusLabel(r.status)}</Badge>
                    </td>
                    <td className="py-1.5 text-xs text-destructive">{r.error ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('workflows.automations.close', 'Close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------

type EditorState = { mode: 'create'; initial: AutomationRuleInput } | { mode: 'edit'; rule: AutomationRule; initial: AutomationRuleInput };

export const AutomationRules: React.FC = () => {
  const { t } = useAppTranslation();
  const queryClient = useQueryClient();
  const isPlatformAdmin = useIsPlatformAdmin();

  const rulesQuery = useQuery({
    queryKey: automationRulesKey,
    queryFn: () => automationRulesApi.list(),
    retry: false,
  });
  const typesQuery = useQuery({
    queryKey: systemEventTypesKey,
    queryFn: () => systemEventsApi.listTypes(),
    staleTime: 5 * 60_000,
  });
  const types = typesQuery.data ?? [];
  const rules = rulesQuery.data ?? [];

  const [editor, setEditor] = useState<EditorState | null>(null);
  const [testing, setTesting] = useState<AutomationRule | null>(null);
  const [history, setHistory] = useState<AutomationRule | null>(null);
  const [deleting, setDeleting] = useState<AutomationRule | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: automationRulesKey });

  const save = useMutation({
    mutationFn: async (input: AutomationRuleInput) =>
      editor?.mode === 'edit' ? automationRulesApi.update(editor.rule.id, input) : automationRulesApi.create(input),
    onSuccess: () => {
      toast.success(t('workflows.automations.saved', 'Automation saved'));
      setEditor(null);
      refresh();
    },
    onError: (err) => {
      toast.error(t('workflows.automations.saveFailed', 'Could not save the automation'), {
        description: automationErrorMessage(err, t),
      });
    },
  });

  const toggle = useMutation({
    mutationFn: (rule: AutomationRule) =>
      automationRulesApi.update(rule.id, { ...ruleToInput(rule), is_active: !rule.is_active }),
    onSuccess: (_d, rule) => {
      toast.success(
        rule.is_active
          ? t('workflows.automations.turnedOff', 'Automation turned off')
          : t('workflows.automations.turnedOn', 'Automation turned on'),
      );
      refresh();
    },
    onError: (err) => {
      toast.error(t('workflows.automations.toggleFailed', 'Could not change the automation'), {
        description: automationErrorMessage(err, t),
      });
    },
  });

  const remove = useMutation({
    mutationFn: (rule: AutomationRule) => automationRulesApi.remove(rule.id),
    onSuccess: () => {
      toast.success(t('workflows.automations.deleted', 'Automation deleted'));
      setDeleting(null);
      refresh();
    },
    onError: (err) => {
      toast.error(t('workflows.automations.deleteFailed', 'Could not delete the automation'), {
        description: automationErrorMessage(err, t),
      });
    },
  });

  const adminOnly = rulesQuery.isError && isAdminOnlyError(rulesQuery.error);

  const conditionSummary = (rule: AutomationRule): string => {
    const c = rule.trigger.conditions;
    const parts: string[] = [];
    if (c.programs.length) parts.push(t('workflows.automations.condPrograms', '{{count}} program(s)', { count: c.programs.length }));
    if (c.assignees.length) parts.push(t('workflows.automations.condAssignees', '{{count}} assignee(s)', { count: c.assignees.length }));
    if (c.repos.length) parts.push(c.repos.join(', '));
    if (c.services.length) parts.push(c.services.join(', '));
    return parts.join(' · ');
  };

  return (
    <div className="space-y-4" data-testid="automation-rules">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold">
            <Wand2 className="h-5 w-5 text-primary" aria-hidden="true" />
            {t('workflows.automations.title', 'Automations')}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t(
              'workflows.automations.description',
              'When something happens in your organization, do something about it: post to a channel, notify staff, create a task or send an email.',
            )}
          </p>
        </div>
        {!adminOnly && (
          <Button
            type="button"
            onClick={() => setEditor({ mode: 'create', initial: emptyRuleInput() })}
            data-testid="automation-new"
          >
            <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
            {t('workflows.automations.new', 'New automation')}
          </Button>
        )}
      </div>

      {rulesQuery.isLoading ? (
        <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          {t('workflows.automations.loading', 'Loading…')}
        </div>
      ) : adminOnly ? (
        <div className="flex items-start gap-3 rounded-lg border p-4" role="alert" data-testid="automation-admin-only">
          <ShieldAlert className="mt-0.5 h-5 w-5 text-muted-foreground" aria-hidden="true" />
          <div className="text-sm">
            <p className="font-medium">{t('workflows.automations.errors.adminOnly', 'Only org admins can manage automations.')}</p>
            <p className="text-muted-foreground">
              {t('workflows.automations.adminOnlyHint', 'Ask an org admin if you need an automation set up.')}
            </p>
          </div>
        </div>
      ) : rulesQuery.isError ? (
        <p className="text-sm text-destructive" role="alert">
          {automationErrorMessage(rulesQuery.error, t)}
        </p>
      ) : rules.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center" data-testid="automation-empty">
          <p className="text-sm font-medium">{t('workflows.automations.empty', 'No automations yet')}</p>
          <p className="text-sm text-muted-foreground">
            {t('workflows.automations.emptyHint', 'For example: when a signature is completed, post to the care team channel.')}
          </p>
        </div>
      ) : (
        <ul className="divide-y rounded-lg border" data-testid="automation-list">
          {rules.map((rule) => {
            const cond = conditionSummary(rule);
            return (
              <li key={rule.id} className="flex flex-wrap items-center gap-3 p-3" data-testid={`automation-row-${rule.id}`}>
                <Switch
                  checked={rule.is_active}
                  onCheckedChange={() => toggle.mutate(rule)}
                  disabled={toggle.isPending}
                  aria-label={t('workflows.automations.toggleLabel', 'Run “{{name}}”', { name: rule.name })}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{rule.name}</span>
                    {!rule.is_active && (
                      <Badge variant="outline" className="text-[10px]">
                        {t('workflows.automations.off', 'Off')}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t('workflows.automations.summary', 'When {{event}} → {{actions}}', {
                      event: rule.trigger.event_type ? typeLabel(rule.trigger.event_type, types, t) : '—',
                      actions: rule.actions.map((a) => actionTypeLabel(a.type, t)).join(', ') || '—',
                    })}
                    {cond && ` · ${cond}`}
                  </p>
                </div>
                <div className="flex gap-1">
                  <Button type="button" variant="ghost" size="sm" onClick={() => setTesting(rule)} data-testid={`automation-test-${rule.id}`}>
                    <FlaskConical className="mr-1 h-4 w-4" aria-hidden="true" />
                    {t('workflows.automations.test', 'Test')}
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setHistory(rule)} data-testid={`automation-history-${rule.id}`}>
                    <History className="mr-1 h-4 w-4" aria-hidden="true" />
                    {t('workflows.automations.history', 'History')}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditor({ mode: 'edit', rule, initial: ruleToInput(rule) })}
                    aria-label={t('workflows.automations.editLabel', 'Edit “{{name}}”', { name: rule.name })}
                    data-testid={`automation-edit-${rule.id}`}
                  >
                    <Pencil className="h-4 w-4" aria-hidden="true" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeleting(rule)}
                    aria-label={t('workflows.automations.deleteLabel', 'Delete “{{name}}”', { name: rule.name })}
                    data-testid={`automation-delete-${rule.id}`}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {editor && (
        <Dialog open onOpenChange={(o) => !o && !save.isPending && setEditor(null)}>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="automation-editor-dialog">
            <DialogHeader>
              <DialogTitle>
                {editor.mode === 'edit'
                  ? t('workflows.automations.editTitle', 'Edit automation')
                  : t('workflows.automations.newTitle', 'New automation')}
              </DialogTitle>
              <DialogDescription>
                {t('workflows.automations.editorDescription', 'Pick the event, narrow it down if you need to, then add what should happen.')}
              </DialogDescription>
            </DialogHeader>
            <AutomationRuleEditor
              initial={editor.initial}
              types={types}
              isPlatformAdmin={isPlatformAdmin}
              saving={save.isPending}
              onSave={(input) => save.mutate(input)}
              onCancel={() => setEditor(null)}
            />
          </DialogContent>
        </Dialog>
      )}

      {testing && <TestDialog rule={testing} onClose={() => setTesting(null)} />}
      {history && <RunsDialog rule={history} types={types} onClose={() => setHistory(null)} />}

      {deleting && (
        <Dialog open onOpenChange={(o) => !o && setDeleting(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{t('workflows.automations.deleteTitle', 'Delete “{{name}}”?', { name: deleting.name })}</DialogTitle>
              <DialogDescription>
                {t('workflows.automations.deleteDescription', 'It stops running right away. This cannot be undone.')}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDeleting(null)} disabled={remove.isPending}>
                {t('workflows.automations.cancel', 'Cancel')}
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() => remove.mutate(deleting)}
                disabled={remove.isPending}
                data-testid="automation-delete-confirm"
              >
                {t('workflows.automations.delete', 'Delete')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default AutomationRules;
